/**
 * Is this the supplier code we already have?
 *
 * Both checks that ask this - the import in importSupplier.js and the form in
 * suppliers.js - used to ask it as `sup.code === parseInt(other)`, which is
 * never true and so never once refused a duplicate. EPAL has two suppliers
 * carrying code 777, SNF Portugal and BRENNTAG PORTUGAL, which is what a check
 * that never fires looks like from the outside.
 *
 * It was wrong three times over, and the type is only the first:
 *
 * - `code` is a TextField, so the left side is a string and the right side a
 *   number. `===` compares types first and stops there.
 * - 31 of the 1103 codes are not numeric - `FD013134`, `XXXX`. parseInt returns
 *   NaN for those, and NaN matches nothing, including itself.
 * - `200XXXX` parses to 200. Coercing the other side instead of this one would
 *   have made it collide with a supplier genuinely coded 200, so the fix is not
 *   to parseInt both sides.
 *
 * Trimmed, because the value on one side came out of a spreadsheet cell.
 * Compared exactly otherwise: nine codes carry a leading zero, and `0777` is
 * not `777` - parseInt would have said it was.
 *
 * Case is left alone deliberately. No two codes in the data differ only in
 * case, so folding it would change nothing today and quietly widen what counts
 * as a duplicate tomorrow.
 */
/**
 * The code as text, whatever Excel decided it was.
 *
 * A code typed into a General cell is stored by Excel as a number, and
 * `sheet_to_json` hands the raw `.v` through, so the same column arrives as a
 * number on one row and a string on the next - `777` and `"FD013134"` in one
 * file. The templates we ship do not even agree with each other: the supplier
 * one stores its codes as numeric cells, the raw-material one as text.
 *
 * This runs in sanitize, not in the Joi schema, and that is the whole point.
 * Joi's converted `.value` is discarded - the caller keeps only
 * `validation.error` and then sends `sanitizedData` - so coercing in the schema
 * would change what is accepted and nothing about what is sent. sanitize is the
 * last code to touch the value before both the validator and the API, which is
 * why `ativo` is normalised there too.
 *
 * `typeof value === 'number'` rather than a truthiness test: a supplier coded
 * `0` exists, and `value ? String(value) : value` would leave that one a number.
 * Anything that is neither number nor string is left alone on purpose, so the
 * schema refuses it by name instead of this quietly turning it into
 * "undefined".
 */
export const codeToString = value => {
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return value;
};

export const sameCode = (a, b) => {
  const left = String(a ?? '').trim();
  const right = String(b ?? '').trim();
  // An empty code is not a duplicate of another empty code - three suppliers
  // have one, and they are not each other.
  return left !== '' && left === right;
};

export default sameCode;
